import { useMemo } from 'react';
import { useBreakpointValue } from '@chakra-ui/react';
import { defineMessages, useIntl } from 'react-intl';
import { type FilterType, filterOrder, useFilter } from '../useFilter';
import { useIsFilterEnabled } from './useIsFilterEnabled';
import type { BloomreachProductType, UseSearchResult } from '../../../hooks/use-search';

const filterLabels = defineMessages({
    category: {
        id: 'search.filters.category',
        defaultMessage: 'Category',
    },
    price: {
        id: 'search.filters.price',
        defaultMessage: 'Price',
    },
    brand: {
        id: 'search.filters.brand',
        defaultMessage: 'Brand',
    },
    cuisineType: {
        id: 'search.filters.course',
        defaultMessage: 'Course',
    },
    material: {
        id: 'search.filters.material',
        defaultMessage: 'Material',
    },
    color: {
        id: 'search.filters.color',
        defaultMessage: 'Color',
    },
    size: {
        id: 'search.filters.size',
        defaultMessage: 'Size',
    },
    rating: {
        id: 'search.filters.rating',
        defaultMessage: 'Rating',
    },
    saving: {
        id: 'search.filters.saving',
        defaultMessage: 'Discount',
    },
});

export const useFilterButtons = (
    search: UseSearchResult,
    productType: BloomreachProductType,
): { id: string; label: string; count: number }[] => {
    const intl = useIntl();
    // Detect if we're on small desktop (between md and lg-sm breakpoints)
    // Small desktop means we have limited screen space and should show fewer filters
    const isSmallDesktop = useBreakpointValue({ base: false, md: true, 'lg-sm': false });
    const isFilterVisible = useIsFilterEnabled();

    // Get base allowed filters for this product type (e.g., recipes have different filters than hardgoods)
    const baseAllowedFilters = useFilter(productType);
    // On small desktop, only show essential filters: category, price, brand, and material
    // Hide less critical filters (color, size, rating, saving) to save screen space
    // On regular desktop, show all allowed filters for the product type
    const allowedFilters = isSmallDesktop
        ? baseAllowedFilters.filter(
              (filter) =>
                  filter === 'category' ||
                  filter === 'price' ||
                  filter === 'brand' ||
                  filter === 'material',
          )
        : baseAllowedFilters;

    return useMemo(() => {
        const filterLabelsMap = {
            category: intl.formatMessage(filterLabels.category),
            price: intl.formatMessage(filterLabels.price),
            brand: intl.formatMessage(filterLabels.brand),
            cuisineType: intl.formatMessage(filterLabels.cuisineType),
            material: intl.formatMessage(filterLabels.material),
            color: intl.formatMessage(filterLabels.color),
            size: intl.formatMessage(filterLabels.size),
            rating: intl.formatMessage(filterLabels.rating),
            saving: intl.formatMessage(filterLabels.saving),
        };

        const filterVisibilityCheck: Record<
            FilterType,
            (searchResult: UseSearchResult) => boolean
        > = {
            category: (searchResult) => (searchResult.filters?.categories?.length ?? 0) > 0,
            price: () => true,
            brand: (searchResult) => (searchResult.filters?.byName?.brand?.length ?? 0) > 0,
            cuisineType: (searchResult) =>
                (searchResult.filters?.byName?.cuisineType?.length ?? 0) > 0,
            material: (searchResult) => (searchResult.filters?.byName?.material?.length ?? 0) > 0,
            color: (searchResult) => (searchResult.filters?.byName?.color?.length ?? 0) > 0,
            size: (searchResult) => (searchResult.filters?.byName?.size?.length ?? 0) > 0,
            rating: () => true,
            saving: () => true,
        };

        return filterOrder
            .filter((filter: FilterType) => {
                // Check if filter is visible based on feature flags
                if (!isFilterVisible(filter)) {
                    return false;
                }

                // First check if filter is allowed for this product type
                if (!allowedFilters.includes(filter)) {
                    return false;
                }

                return filterVisibilityCheck[filter](search);
            })
            .map((filter: FilterType) => ({
                id: filter,
                label: filterLabelsMap[filter],
                count: search.activeFilters.filter((f) => f.type === filter).length,
            }));
    }, [intl, isFilterVisible, allowedFilters, search]);
};
